// Case studies data — keep separate from rendering for clarity.
import {
  GraduationCap,
  Building2,
  Stethoscope,
  ShoppingBag,
  Banknote,
  Briefcase,
  Truck,
} from "lucide-react";

export const CASE_STUDIES = [
  {
    id: "edtech",
    slug: "edtech",
    label: "EdTech",
    icon: GraduationCap,
    badge: "EDTECH · OPEN UNIVERSITY",
    headline:
      "How Vidyasetu University Automated 85% of Student Inquiries With a Single AI Voice Agent",
    description:
      "A state-recognised open university with 1.5 lakh annual enrollments across 40+ distance learning programmes was drowning in inquiry calls. With only 28 admission counselors and 3 admission seasons a year, over 60% of student queries went unanswered during peak periods.",
    topMetrics: [
      { value: "85%", label: "Queries Handled by AI" },
      { value: "4×", label: "Enrollment Conversion" },
      { value: "18 days", label: "Live in" },
    ],
    profile: [
      ["Organisation", "Vidyasetu Open University"],
      ["Type", "State-recognised Open University"],
      ["Annual Enrollments", "1,50,000+ students"],
      ["Programmes", "40+ UG, PG & Certificate"],
      ["Delivery Mode", "Distance Learning + Hybrid"],
      ["Languages Handled", "Hindi, English, Marathi"],
    ],
    problems: [
      "Over 1,200 inbound calls/day during admission season — 28 counselors handling <30% of volume",
      "Students asking repetitive questions: eligibility, fees, last date to apply, exam schedule",
      "Application status calls — students calling 3–4 times to check document verification status",
      "Distance learning portal login issues — reset password and access issues",
      "Zero follow-up on incomplete applications — 22,000 students drop off mid-application each year",
    ],
    workflow: [
      {
        n: 1,
        title: "Student Calls Helpline",
        body: "AI answers instantly, 24/7 — no hold music",
      },
      {
        n: 2,
        title: "Intent Detection",
        body: "AI identifies admission, status, course info, or tech help",
      },
      {
        n: 3,
        title: "Live Data Lookup",
        body: "Pulls application status, eligibility, fees via API in real time",
      },
      {
        n: 4,
        title: "Resolve or Escalate",
        body: "85% resolved; complex cases warm-transferred to counselor",
      },
      {
        n: 5,
        title: "WhatsApp Follow-up",
        body: "Brochure, application link, deadline reminder sent automatically",
      },
    ],
    channels: [
      { ch: "WhatsApp", text: "Brochure + Application Link" },
      { ch: "Email", text: "Personalised Course Details PDF" },
      { ch: "SMS", text: "Admission deadline reminder D-7 & D-1" },
      { ch: "Outbound", text: "Auto-call incomplete applications at D-3" },
      { ch: "Warm Transfer", text: "Context whispered to counselor before connect" },
    ],
    results: [
      {
        value: "85%",
        label: "Queries resolved by AI without human intervention",
        delta: "↑ from 28%",
      },
      {
        value: "4.2×",
        label: "Enrollment conversion from inquiry to admission",
        delta: "↑ from 1.1×",
      },
      {
        value: "₹38L",
        label: "Annual counselor cost avoided",
        delta: "↑ Reinvested into counselor upskilling",
      },
      {
        value: "91%",
        label: "Student satisfaction score (CSAT)",
        delta: "↑ from 64%",
      },
    ],
    testimonial: {
      quote:
        "During our peak admission month, we used to have students on hold for 40 minutes. Now the AI handles everything instantly in Hindi and English — course details, fees, eligibility, even application status lookups. Our counselors only speak to students who are genuinely ready to enroll. Conversion went up 4× and our team finally has time to do meaningful work.",
      name: "Prof. Arvind Kulkarni",
      role: "Registrar, Vidyasetu Open University",
    },
    footerLine: "18-day deployment · 85% query automation · 4.2× enrollment conversion",
  },
  {
    id: "real-estate",
    slug: "real-estate",
    label: "Real Estate",
    icon: Building2,
    badge: "REAL ESTATE · PREMIUM DEVELOPER",
    headline:
      "How a Premium Developer Qualified 12,000+ Property Leads With AI Voice + WhatsApp",
    description:
      "A leading real-estate developer in the MMR region with 6 active projects was receiving 600+ leads/day from Facebook, MagicBricks, and 99acres. With 18 sales agents, less than 18% of leads got a follow-up call within 2 hours — killing site-visit conversion.",
    topMetrics: [
      { value: "72%", label: "Leads Qualified by AI" },
      { value: "3.8×", label: "Site-visit Conversion" },
      { value: "12 days", label: "Live in" },
    ],
    profile: [
      ["Organisation", "Lumeon Properties"],
      ["Segment", "Premium · MMR region"],
      ["Active Projects", "6 (3 RTM, 3 under-construction)"],
      ["Daily Leads", "600+ across portals"],
      ["Sales Agents", "18 (across 3 zones)"],
      ["Languages Handled", "English, Hindi, Marathi"],
    ],
    problems: [
      "Leads from 5 portals (FB, MagicBricks, 99acres, organic, walk-ins) — no unified pipeline",
      "Average first-touch time was 6–8 hours — by then leads had spoken to 3 competitors",
      "Sales agents wasted ~60% of time on tyre-kickers, missing budget-qualified buyers",
      "No nurture for cold leads — 78% dropped off after one missed call",
      "Manual CRM updates lagged by 2–3 days, breaking handoff to senior closers",
    ],
    workflow: [
      { n: 1, title: "Lead Received", body: "From FB / MagicBricks / 99acres in real-time" },
      { n: 2, title: "AI Calls in 60s", body: "Bilingual greeting · introduces project · checks intent" },
      { n: 3, title: "Smart Qualification", body: "Budget · location · timeline · loan-readiness captured" },
      { n: 4, title: "WhatsApp + Site-Visit", body: "Brochure auto-sent · slot booked on agent calendar" },
      { n: 5, title: "CRM + Handoff", body: "Hot leads warm-transferred · cold leads added to drip" },
    ],
    channels: [
      { ch: "WhatsApp", text: "Project brochure + floor plans" },
      { ch: "Calendar", text: "Auto-booked site-visit slots" },
      { ch: "SMS", text: "Day-before site-visit reminder" },
      { ch: "Outbound", text: "Drip-call cold leads every 7 days" },
      { ch: "Warm Transfer", text: "Hot leads handed to closer with summary" },
    ],
    results: [
      { value: "72%", label: "Leads qualified by AI within 60 seconds", delta: "↑ from 18%" },
      { value: "3.8×", label: "Site-visit conversion rate", delta: "↑ from 0.9×" },
      { value: "47%", label: "Lower cost-per-qualified-lead", delta: "↓ Saves ₹62L annually" },
      { value: "9 min", label: "Average lead response time", delta: "↓ from 6.4 hrs" },
    ],
    testimonial: {
      quote:
        "Earlier, our sales team would call leads 6–8 hours after they enquired — by then they'd already spoken to three competitors. Now Fawks AI calls every lead in under a minute, qualifies them, and books site visits. Site-visit conversion is up 3.8×, and our closers only get hot leads.",
      name: "Priya Shah",
      role: "VP Sales, Lumeon Properties",
    },
    footerLine: "12-day deployment · 72% AI qualification · 3.8× site-visit conversion",
  },
  {
    id: "healthcare",
    slug: "healthcare",
    label: "Healthcare",
    icon: Stethoscope,
    badge: "HEALTHCARE · CLINIC NETWORK",
    headline:
      "How a Clinic Network Automated Appointment Calls and Cut No-Shows by 41%",
    description:
      "A multi-specialty clinic chain across 14 cities with 70,000 monthly appointments was losing ₹2.4 Cr/year to no-shows. Manual confirmation calls couldn't scale — only 38% of patients were reached the day before their appointment.",
    topMetrics: [
      { value: "68%", label: "Calls Automated" },
      { value: "41%", label: "No-show Reduction" },
      { value: "10 days", label: "Live in" },
    ],
    profile: [
      ["Organisation", "MediCircle Clinics"],
      ["Type", "Multi-specialty clinic network"],
      ["Footprint", "14 cities · 38 centres"],
      ["Monthly Appointments", "70,000+"],
      ["Doctors", "220 across 11 specialties"],
      ["Languages Handled", "Hindi, English, Tamil, Telugu"],
    ],
    problems: [
      "38% no-show rate costing ~₹2.4 Cr/year in unused doctor slots",
      "Manual confirmation only reached 38% of patients — staff couldn't scale",
      "No proactive reschedule path — patients had to call back and were dropped into IVR",
      "Lab-test and prescription-refill follow-ups completely missed",
      "After-hours bookings unattended — leaked to competitors",
    ],
    workflow: [
      { n: 1, title: "Appointment Booked", body: "AI auto-triggered 24h before slot" },
      { n: 2, title: "Multilingual Confirm Call", body: "Patient confirms / requests reschedule" },
      { n: 3, title: "Live Reschedule", body: "AI offers next 3 slots and books on the call" },
      { n: 4, title: "Day-of Reminder", body: "WhatsApp + SMS sent with directions" },
      { n: 5, title: "Post-visit Follow-up", body: "Prescription refill check + CSAT" },
    ],
    channels: [
      { ch: "WhatsApp", text: "Appointment confirmation + map" },
      { ch: "SMS", text: "Day-of reminder with token" },
      { ch: "Calendar", text: "Sync to doctor schedules" },
      { ch: "IVR Fallback", text: "If unreachable, retry 3× in 6 hours" },
      { ch: "Warm Transfer", text: "Routed to front-desk for clinical queries" },
    ],
    results: [
      { value: "68%", label: "Confirmation calls fully automated", delta: "↑ from 38%" },
      { value: "41%", label: "Lower no-show rate", delta: "↓ from 38% to 22%" },
      { value: "22%", label: "Rebookings recovered same-week", delta: "↑ ₹1.6 Cr revenue saved" },
      { value: "4.7", label: "Patient satisfaction (CSAT) out of 5", delta: "↑ from 4.1" },
    ],
    testimonial: {
      quote:
        "Our no-show rate dropped from 38% to 22% in 60 days. The AI calls patients in their preferred language, accepts reschedules on the call, and even helps with prescription refills. We've reinvested the savings into better doctor tools and patient care.",
      name: "Dr. Neha Reddy",
      role: "Operations Head, MediCircle Clinics",
    },
    footerLine: "10-day deployment · 68% calls automated · 41% fewer no-shows",
  },
  {
    id: "ecommerce",
    slug: "ecommerce",
    label: "D2C / E-com",
    icon: ShoppingBag,
    badge: "D2C · BEAUTY BRAND",
    headline:
      "How a D2C Beauty Brand Recovered 23% of Abandoned Carts With AI Calls",
    description:
      "A fast-growing D2C beauty brand on Shopify with ₹35 Cr ARR was losing 71% of carts at checkout. Email reminders had a 1.2% recovery rate. WhatsApp templates plateaued at 3.4%. Retargeting ads were getting more expensive every quarter.",
    topMetrics: [
      { value: "23%", label: "Cart Recovery Rate" },
      { value: "5.1×", label: "ROAS on AI Calls" },
      { value: "9 days", label: "Live in" },
    ],
    profile: [
      ["Organisation", "Velora Beauty"],
      ["Stack", "Shopify + Yotpo + Klaviyo"],
      ["Revenue", "₹35 Cr ARR"],
      ["Monthly Carts", "1.4 lakh"],
      ["Abandonment Rate", "71% (pre-Fawks)"],
      ["Languages Handled", "English, Hindi"],
    ],
    problems: [
      "71% cart abandonment — emails recovered only 1.2%",
      "COD-confirmation friction caused 18% RTO on order-bound carts",
      "Customer acquisition cost rising 22% YoY on Meta retargeting",
      "Repeat-purchase rate plateaued at 19% — no post-purchase touchpoint",
      "Reviews collection broken — only 8% of customers left reviews",
    ],
    workflow: [
      { n: 1, title: "Cart Abandoned (15 min)", body: "AI triggered with offer eligibility check" },
      { n: 2, title: "Personalised AI Call", body: "Refers to cart items, offers nudge / discount" },
      { n: 3, title: "WhatsApp Checkout Link", body: "Single-click resume with applied coupon" },
      { n: 4, title: "COD Confirmation", body: "Voice + WhatsApp verify before dispatch" },
      { n: 5, title: "Post-purchase Loop", body: "Delivery tracking + review request" },
    ],
    channels: [
      { ch: "WhatsApp", text: "Checkout link with applied coupon" },
      { ch: "Email", text: "PDF invoice + care guide" },
      { ch: "SMS", text: "Order tracking link" },
      { ch: "Outbound", text: "Day-3 review request call" },
      { ch: "Warm Transfer", text: "Support for product queries" },
    ],
    results: [
      { value: "23%", label: "Abandoned carts recovered", delta: "↑ from 3.4% (WA only)" },
      { value: "5.1×", label: "ROAS on AI recovery calls", delta: "↑ vs 1.8× Meta retargeting" },
      { value: "14%", label: "Lower COD-RTO rate", delta: "↓ from 18% to 4%" },
      { value: "+28%", label: "Repeat-purchase rate uplift", delta: "↑ from 19% to 24%" },
    ],
    testimonial: {
      quote:
        "We were spending ₹4L/month on Meta retargeting ads with declining returns. Fawks AI recovers 23% of our abandoned carts at one-fifth the cost. The conversation feels human — customers tell us they thought it was a real person from our team.",
      name: "Karan Mehta",
      role: "Head of Growth, Velora Beauty",
    },
    footerLine: "9-day deployment · 23% cart recovery · 5.1× ROAS",
  },
  {
    id: "bfsi",
    slug: "bfsi",
    label: "BFSI",
    icon: Banknote,
    badge: "BFSI · NBFC LENDER",
    headline:
      "How an NBFC Automated Collection Reminders and Cut Roll-Forward by 18%",
    description:
      "A mid-sized NBFC with ₹1,800 Cr AUM was making 45,000 collection calls/month with a 32-person team. Accounts dropping into DPD 30+ buckets were costing ₹6.2 Cr/year in roll-forward losses — and TRAI calling-window compliance was a constant audit risk.",
    topMetrics: [
      { value: "61%", label: "Calls Resolved by AI" },
      { value: "29%", label: "Manual Effort Reduced" },
      { value: "15 days", label: "Live in" },
    ],
    profile: [
      ["Organisation", "ProsperLend Finance"],
      ["Type", "NBFC · Personal & SME lending"],
      ["AUM", "₹1,800 Cr"],
      ["Monthly Collection Calls", "45,000+"],
      ["Recovery Agents", "32 across 4 bureaus"],
      ["Languages Handled", "Hindi, English, Marathi, Kannada, Tamil"],
    ],
    problems: [
      "DPD 30+ roll-forward bucket costing ₹6.2 Cr/year",
      "Calling-window compliance gaps with TRAI guidelines",
      "Scripts not empathy-tuned — borrowers disengaged or escalated to lawyers",
      "Promise-to-Pay capture inconsistent — 30% PTPs missed CRM logging",
      "No nighttime / weekend coverage — high-intent payers fell through",
    ],
    workflow: [
      { n: 1, title: "DPD 5 · Gentle Reminder", body: "AI calls with empathetic script in regional language" },
      { n: 2, title: "DPD 15 · PTP Capture", body: "Promise-to-Pay date logged · WhatsApp payment link sent" },
      { n: 3, title: "DPD 30 · Escalation", body: "Compliance-aware tone shift · settlement option offered" },
      { n: 4, title: "PTP Broken · Warm Transfer", body: "Hand-off to agent with full conversation context" },
      { n: 5, title: "Post-resolution Log", body: "Full CRM update · TRAI window audit trail" },
    ],
    channels: [
      { ch: "WhatsApp", text: "Payment link + EMI breakdown" },
      { ch: "SMS", text: "PTP confirmation receipt" },
      { ch: "IVR Fallback", text: "Auto-debit reminder if line busy" },
      { ch: "Outbound", text: "Pre-due-date courtesy nudge at DPD-3" },
      { ch: "Warm Transfer", text: "Agent connect with case summary" },
    ],
    results: [
      { value: "61%", label: "Accounts resolved by AI without human agent", delta: "↑ from 22%" },
      { value: "29%", label: "Recovery-agent capacity freed", delta: "↑ refocused on negotiation cases" },
      { value: "₹4.1 Cr", label: "DPD-30+ roll-forward avoided annually", delta: "↑ 18% bucket reduction" },
      { value: "100%", label: "TRAI calling-window compliance", delta: "↑ from 91% audit score" },
    ],
    testimonial: {
      quote:
        "Our DPD 30+ bucket dropped 18% in four months. The AI handles routine reminders with empathy in regional languages — our agents now focus on accounts that genuinely need negotiation. Our compliance team is happier too — TRAI window audit is fully automated.",
      name: "Rakesh Iyer",
      role: "Chief Risk Officer, ProsperLend Finance",
    },
    footerLine: "15-day deployment · 61% AI resolution · ₹4.1 Cr roll-forward saved",
  },
  {
    id: "hr-recruitment",
    slug: "hr-recruitment",
    label: "HR & Recruit",
    icon: Briefcase,
    badge: "HR · TECH RECRUITMENT",
    headline:
      "How a Tech Staffing Firm Screened Candidates 6× Faster With AI Voice",
    description:
      "A mid-tier IT staffing firm with 220 active openings/month was burning 14 recruiters on first-call screening — a process that took 2–3 days per role and routinely missed candidates who applied after working hours.",
    topMetrics: [
      { value: "74%", label: "Candidates Screened by AI" },
      { value: "6×", label: "Faster Shortlisting" },
      { value: "8 days", label: "Live in" },
    ],
    profile: [
      ["Organisation", "CodeWeave Staffing"],
      ["Domain", "IT staffing · contract & permanent"],
      ["Monthly Openings", "220 active roles"],
      ["Recruiter Team", "14 across 3 verticals"],
      ["Avg. Applicants/Role", "84"],
      ["Languages Handled", "Hindi, English"],
    ],
    problems: [
      "First-call screening took 2–3 days per role — best candidates went to competitors",
      "After-hours applicants ignored — 38% of applications come 7pm–11pm",
      "Recruiters fatigued by repetitive Q&A: notice period, CTC, location, tech stack",
      "Hiring-manager handoff incomplete — shortlists missed key context",
      "Candidate experience score sliding (3.9/5) — Glassdoor reviews getting hit",
    ],
    workflow: [
      { n: 1, title: "Candidate Applies", body: "AI triggered within 5 minutes of resume submission" },
      { n: 2, title: "JD-aware Screening", body: "Asks role-specific questions · captures notice / CTC / location" },
      { n: 3, title: "Fit Scoring", body: "AI scores against JD requirements and flags top 20%" },
      { n: 4, title: "Interview Booking", body: "Books slot on hiring-manager calendar live" },
      { n: 5, title: "Recruiter Handoff", body: "Full transcript + score + flag-list passed forward" },
    ],
    channels: [
      { ch: "WhatsApp", text: "JD PDF + interview details" },
      { ch: "Calendar", text: "Auto-book on hiring-manager slots" },
      { ch: "Email", text: "Interview invite with prep links" },
      { ch: "Outbound", text: "Reminder call 24h before interview" },
      { ch: "Warm Transfer", text: "Senior recruiter for compensation negotiation" },
    ],
    results: [
      { value: "74%", label: "Candidates screened end-to-end by AI", delta: "↑ from 12%" },
      { value: "6×", label: "Faster shortlist turnaround", delta: "↓ 3 days to 8 hours" },
      { value: "38%", label: "Recruiter capacity freed for senior closing", delta: "↑ 5.3 FTE equivalent" },
      { value: "4.8", label: "Candidate experience score / 5", delta: "↑ from 3.9" },
    ],
    testimonial: {
      quote:
        "Our recruiters were drowning in first-call screening — same questions every time. Fawks AI now does that screening in under 6 minutes, even at 11pm. Our hiring managers see qualified shortlists by next morning, and our candidate experience score is at an all-time high.",
      name: "Anisha Krishnan",
      role: "Head of Talent, CodeWeave Staffing",
    },
    footerLine: "8-day deployment · 74% AI screening · 6× faster shortlists",
  },
  {
    id: "logistics",
    slug: "logistics",
    label: "Logistics",
    icon: Truck,
    badge: "LOGISTICS · LAST-MILE DELIVERY",
    headline:
      "How a Last-Mile Logistics Partner Automated 82% of Delivery Calls",
    description:
      "A last-mile delivery partner for 12 major e-commerce brands was handling 280,000 deliveries/day across 65 cities. Customer-care calls about delivery status, address verification, and delay reasons were costing ₹1.1 Cr/year in support overhead.",
    topMetrics: [
      { value: "82%", label: "Calls Automated" },
      { value: "38%", label: "Support Ticket Reduction" },
      { value: "11 days", label: "Live in" },
    ],
    profile: [
      ["Organisation", "TrackEdge Logistics"],
      ["Service", "Last-mile · B2B2C delivery"],
      ["E-com Partners", "12 (Shopify · Flipkart · Meesho)"],
      ["Daily Deliveries", "2,80,000+"],
      ["Cities Served", "65 (Tier 1 + 2 + 3)"],
      ["Languages Handled", "Hindi, English, Bengali, Tamil, Telugu, Marathi"],
    ],
    problems: [
      "Customer-care cost of ₹1.1 Cr/year on 'where is my package?' calls",
      "28% of support calls were only address-verification issues",
      "RTO (return-to-origin) at 9.3% — drivers couldn't reach customers in time",
      "No proactive communication for delay events (weather, route, vehicle)",
      "Brand-partners losing CSAT — being penalised on Flipkart / Shopify ratings",
    ],
    workflow: [
      { n: 1, title: "Delivery Scheduled", body: "AI auto-confirms address + slot 24h before" },
      { n: 2, title: "Proactive Delay Alert", body: "Route/weather event triggers AI call to customer" },
      { n: 3, title: "On-call Reschedule", body: "Customer reschedules slot on the call" },
      { n: 4, title: "Delivery-day Reminder", body: "ETA + driver number sent via WhatsApp + SMS" },
      { n: 5, title: "COD Verification", body: "Voice + WhatsApp confirm before vehicle dispatch" },
    ],
    channels: [
      { ch: "WhatsApp", text: "Real-time tracking link" },
      { ch: "SMS", text: "Delivery slot reminder" },
      { ch: "IVR ETA Query", text: "24×7 self-serve ETA lookup" },
      { ch: "Outbound", text: "Proactive delay calls" },
      { ch: "Warm Transfer", text: "Customer-care for damaged-package issues" },
    ],
    results: [
      { value: "82%", label: "Delivery-related calls automated", delta: "↑ from 14%" },
      { value: "38%", label: "Drop in customer-care support tickets", delta: "↓ ₹42L saved annually" },
      { value: "21%", label: "RTO rate reduction", delta: "↓ from 9.3% to 7.3%" },
      { value: "₹84L", label: "Customer-care cost savings annually", delta: "↑ Reinvested into ops" },
    ],
    testimonial: {
      quote:
        "Customers used to flood our support line with 'where's my package?' calls. Now Fawks AI proactively calls them with ETA updates and handles address verifications in regional languages. Our team focuses only on genuine escalations — damaged packages, refund cases.",
      name: "Vikram Sengupta",
      role: "VP Operations, TrackEdge Logistics",
    },
    footerLine: "11-day deployment · 82% calls automated · 38% fewer support tickets",
  },
];

export const CASE_STUDY_SLUGS = CASE_STUDIES.map((c) => c.slug);
